export type PromotionComponentInput = {
	code: string;
	percentage: number;
	amount?: number | null;
	sourceType?: string | null;
	sourceSubtype?: string | null;
};

export type RatePromotionsInput = {
	components: readonly PromotionComponentInput[];
	combinedPercentage: number;
};

export type PromotionTooltipPayload = {
	combined?: string;
	total?: string;
	components: {
		label: string;
		percentage: string;
		amount?: string;
		bookingFunded: boolean;
		note: string;
	}[];
	tier?: {
		label: string;
		plus: boolean;
	};
};

export type PromotionIndicatorState = {
	visible: boolean;
	hasPromotion: boolean;
	hotelFunded: boolean;
	mixedFunding: boolean;
	showTier: boolean;
	funding: 'hotel' | 'booking' | 'tier' | undefined;
};

export const hasPreferredTier = (preferredTier?: string | null): preferredTier is string =>
	preferredTier != null && preferredTier.length > 0 && preferredTier !== 'NONE';

export const getPromotionIndicatorState = (
	promotions?: RatePromotionsInput | null,
	preferredTier?: string | null
): PromotionIndicatorState => {
	const components = promotions?.components ?? [];
	const hasPromotion = components.length > 0;
	const hotelFunded = components.some(component => component.code !== 'BOOKING_FUNDED');
	const mixedFunding =
		hotelFunded && components.some(component => component.code === 'BOOKING_FUNDED');
	const showTier = hasPreferredTier(preferredTier);

	return {
		visible: hasPromotion || showTier,
		hasPromotion,
		hotelFunded,
		mixedFunding,
		showTier,
		funding: hasPromotion ? (hotelFunded ? 'hotel' : 'booking') : showTier ? 'tier' : undefined,
	};
};

export const buildPromotionTooltipPayload = (
	promotions?: RatePromotionsInput | null,
	preferredTier?: string | null,
	formatAmount?: (amount: number) => string
): PromotionTooltipPayload => {
	const payload: PromotionTooltipPayload = { components: [] };
	const components = [...(promotions?.components ?? [])].sort(
		(a, b) => b.percentage - a.percentage
	);

	if (promotions != null && components.length > 0) {
		payload.combined = `${formatPercentage(promotions.combinedPercentage)} off`;
		const amounts = components.map(component => component.amount);
		if (formatAmount != null && amounts.every((amount): amount is number => amount != null)) {
			payload.total = `-${formatAmount(amounts.reduce((sum, amount) => sum + amount, 0))}`;
		}
		payload.components = components.map(component => ({
			label: promotionLabel(component),
			percentage: formatPercentage(component.percentage),
			amount:
				formatAmount != null && component.amount != null
					? `-${formatAmount(component.amount)}`
					: undefined,
			bookingFunded: component.code === 'BOOKING_FUNDED',
			note:
				component.code === 'BOOKING_FUNDED'
					? "Funded by Booking.com — the hotel's net rate is unchanged"
					: 'The hotel runs this deal',
		}));
	}

	if (hasPreferredTier(preferredTier)) {
		payload.tier = {
			label: preferredTierLabel(preferredTier),
			plus: preferredTier === 'PREFERRED_PLUS',
		};
	}

	return payload;
};

export const promotionAriaLabel = (payload: PromotionTooltipPayload): string => {
	const parts: string[] = [];
	if (payload.combined != null) {
		parts.push(
			`${payload.combined}: ${payload.components
				.map(component => `${component.label} ${component.percentage}`)
				.join(', ')}`
		);
	}
	if (payload.tier != null) parts.push(`${payload.tier.label} partner`);
	return parts.join('; ');
};

const formatPercentage = (percentage: number): string =>
	`${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(percentage)}%`;

const promotionLabel = (component: PromotionComponentInput): string => {
	switch (component.code) {
		case 'GETAWAY_DEAL':
			return 'Getaway Deal';
		case 'BONUS_SAVINGS':
			return 'Bonus savings';
		case 'LAST_MINUTE_DEAL':
			return 'Last-minute Deal';
		case 'EARLY_BOOKER_DEAL':
			return 'Early Booker Deal';
		case 'LIMITED_TIME_DEAL':
			return 'Limited-time Deal';
		case 'WEEKLY_RATE':
			return 'Weekly rate';
		case 'BOOKING_FUNDED':
			return 'Booking.com pays';
		case 'UNKNOWN_PROMOTION':
		default:
			if (component.sourceSubtype != null && component.sourceSubtype.length > 0)
				return component.sourceSubtype;
			if (component.sourceType != null && component.sourceType.length > 0)
				return component.sourceType;
			return 'Unknown promotion';
	}
};

const preferredTierLabel = (preferredTier: string): string => {
	switch (preferredTier) {
		case 'PREFERRED_PLUS':
			return 'Preferred Plus';
		case 'PREFERRED':
			return 'Preferred';
		default:
			return preferredTier;
	}
};
